import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "reports-page" }
const _hoisted_2 = { class: "reports-body" }
const _hoisted_3 = { class: "reports-main" }
const _hoisted_4 = { class: "reports-section" }
const _hoisted_5 = { class: "reports-section-header" }
const _hoisted_6 = {
  key: 0,
  class: "reports-search"
}
const _hoisted_7 = {
  key: 0,
  class: "reports-error"
}
const _hoisted_8 = {
  key: 1,
  class: "reports-empty"
}
const _hoisted_9 = {
  key: 0,
  class: "reports-search-results"
}
const _hoisted_10 = {
  key: 1,
  class: "reports-empty"
}
const _hoisted_11 = {
  class: "reports-tabs",
  role: "tablist"
}
const _hoisted_12 = ["aria-selected", "onClick"]
const _hoisted_13 = {
  key: 0,
  class: "reports-tab-content"
}
const _hoisted_14 = {
  key: 0,
  class: "reports-ungrouped"
}
const _hoisted_15 = {
  key: 1,
  class: "reports-empty"
}

import { computed, onMounted, ref } from "vue";
import Page from "@/components/layout/Page.vue";
import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";
import Loader from "@/components/Loader.vue";
import ReportFolderAccordion from "@/components/reports/ReportFolderAccordion.vue";
import ReportRow from "@/components/reports/ReportRow.vue";
import ReportViewerModal from "@/components/reports/ReportViewerModal.vue";
import ReportsSidebar from "@/components/reports/ReportsSidebar.vue";
import { useReportsStore } from "@/store/store/reportsStore";
import { $api } from "@/services/api1";
import functions from "@/use/functions";
import type { ReportEmbedToken, ReportFolder, ReportItem, ReportExportFormat } from "@/types/reports";

interface SearchResult {
  report: ReportItem;
  breadcrumb: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Reports',
  setup(__props) {

const reportsStore = useReportsStore();

const searchQuery = ref<string>("");

const searchResults = computed<SearchResult[]>(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];

  const results: SearchResult[] = [];
  for (const tab of reportsStore.tabs) {
    for (const folder of tab.folders) {
      for (const report of folder.reports) {
        if (report.displayName.toLowerCase().includes(q)) {
          results.push({ report, breadcrumb: `${tab.name} › ${folder.name}` });
        }
      }
    }
    for (const report of tab.reports) {
      if (report.displayName.toLowerCase().includes(q)) {
        results.push({ report, breadcrumb: tab.name });
      }
    }
  }
  return results;
});

const activeReport = ref<ReportItem | null>(null);
const activeToken = ref<ReportEmbedToken | null>(null);
const viewerOpen = ref<boolean>(false);

const activeTab = computed(() => reportsStore.activeTab);

const sortedFolders = computed<ReportFolder[]>(() =>
  activeTab.value
    ? [...activeTab.value.folders].sort((a, b) => a.sortOrder - b.sortOrder)
    : []
);

const ungroupedReports = computed<ReportItem[]>(() =>
  activeTab.value
    ? [...activeTab.value.reports].sort((a, b) => a.sortOrder - b.sortOrder)
    : []
);

function showReportError(): void {
  functions.openModal({
    title: "Oops!",
    description:
      "There was an issue opening the report. Please try again later or contact Member Support.",
  });
}

async function openReport(report: ReportItem): Promise<void> {
  try {
    const token = await $api.reports.getEmbedToken(report.id);

    if (!token || !token.token) {
      showReportError();
      return;
    }

    reportsStore.logRecentlyViewed(report);
    activeReport.value = report;
    activeToken.value = token;
    viewerOpen.value = true;
  } catch {
    showReportError();
  }
}

async function openReportById(id: number): Promise<void> {
  // Find the report in the catalog so we have its full object for the modal
  for (const tab of reportsStore.tabs) {
    for (const folder of tab.folders) {
      const found = folder.reports.find((r) => r.id === id);
      if (found) { await openReport(found); return; }
    }
    const found = tab.reports.find((r) => r.id === id);
    if (found) { await openReport(found); return; }
  }
}

function closeReport(): void {
  viewerOpen.value = false;
  activeReport.value = null;
  activeToken.value = null;
}

async function onDownload(report: ReportItem, format: ReportExportFormat): Promise<void> {
  await reportsStore.downloadReport(report, format);
}

onMounted(async () => {
  reportsStore.loadRecentlyViewed();
  await reportsStore.loadDashboard();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Page, null, {
    default: _withCtx(() => [
      _createVNode(Breadcrumbs, { "root-path": "/reports" }),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _cache[2] || (_cache[2] = _createElementVNode("h2", { class: "reports-section-title" }, "Reports", -1)),
                (!_unref(reportsStore).isLoading && !_unref(reportsStore).error && _unref(reportsStore).tabs.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                        type: "text",
                        placeholder: "Search reports...",
                        class: "reports-search-input",
                        "aria-label": "Search reports"
                      }, null, 512), [
                        [_vModelText, searchQuery.value]
                      ]),
                      (searchQuery.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            type: "button",
                            class: "reports-search-clear",
                            "aria-label": "Clear search",
                            onClick: _cache[1] || (_cache[1] = ($event: any) => (searchQuery.value = ''))
                          }, "✕"))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              (_unref(reportsStore).isLoading)
                ? (_openBlock(), _createBlock(Loader, { key: 0 }))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (_unref(reportsStore).error)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_unref(reportsStore).error), 1))
                      : (_unref(reportsStore).tabs.length === 0)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_8, " No reports are available. "))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            (searchQuery.value.trim())
                              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                  (searchResults.value.length > 0)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                                        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "report-row-header" }, [
                                          _createElementVNode("span", { class: "col-name" }, "Report Name"),
                                          _createElementVNode("span", { class: "col-location" }, "Location"),
                                          _createElementVNode("span", { class: "col-actions" })
                                        ], -1)),
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchResults.value, (result) => {
                                          return (_openBlock(), _createBlock(ReportRow, {
                                            key: result.report.id,
                                            report: result.report,
                                            breadcrumb: result.breadcrumb,
                                            onSelect: openReport,
                                            onToggleFavorite: _unref(reportsStore).toggleFavorite,
                                            onDownload: onDownload
                                          }, null, 8, ["report", "breadcrumb", "onToggleFavorite"]))
                                        }), 128))
                                      ]))
                                    : (_openBlock(), _createElementBlock("p", _hoisted_10, "No reports match your search."))
                                ], 64))
                              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                  _createElementVNode("div", _hoisted_11, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(reportsStore).tabs, (tab) => {
                                      return (_openBlock(), _createElementBlock("button", {
                                        key: tab.id,
                                        type: "button",
                                        role: "tab",
                                        class: _normalizeClass(['reports-tab', { active: tab.id === _unref(reportsStore).activeTabId }]),
                                        "aria-selected": tab.id === _unref(reportsStore).activeTabId ? 'true' : 'false',
                                        onClick: ($event: any) => (_unref(reportsStore).setActiveTab(tab.id))
                                      }, _toDisplayString(tab.name), 11, _hoisted_12))
                                    }), 128))
                                  ]),
                                  (activeTab.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sortedFolders.value, (folder) => {
                                          return (_openBlock(), _createBlock(ReportFolderAccordion, {
                                            key: folder.id,
                                            folder: folder,
                                            onSelect: openReport,
                                            onToggleFavorite: _unref(reportsStore).toggleFavorite,
                                            onDownload: onDownload
                                          }, null, 8, ["folder", "onToggleFavorite"]))
                                        }), 128)),
                                        (ungroupedReports.value.length > 0)
                                          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                              _cache[4] || (_cache[4] = _createElementVNode("div", { class: "report-row-header" }, [
                                                _createElementVNode("span", { class: "col-name" }, "Report Name"),
                                                _createElementVNode("span", { class: "col-refreshed" }, "Last Refreshed"),
                                                _createElementVNode("span", { class: "col-actions" })
                                              ], -1)),
                                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ungroupedReports.value, (report) => {
                                                return (_openBlock(), _createBlock(ReportRow, {
                                                  key: report.id,
                                                  report: report,
                                                  onSelect: openReport,
                                                  onToggleFavorite: _unref(reportsStore).toggleFavorite,
                                                  onDownload: onDownload
                                                }, null, 8, ["report", "onToggleFavorite"]))
                                              }), 128))
                                            ]))
                                          : _createCommentVNode("", true),
                                        (sortedFolders.value.length === 0 && ungroupedReports.value.length === 0)
                                          ? (_openBlock(), _createElementBlock("p", _hoisted_15, " No reports in this tab. "))
                                          : _createCommentVNode("", true)
                                      ]))
                                    : _createCommentVNode("", true)
                                ], 64))
                          ], 64))
                  ], 64))
            ])
          ]),
          _createVNode(ReportsSidebar, {
            onOpenReport: openReport,
            onOpenById: openReportById
          })
        ])
      ]),
      _createVNode(ReportViewerModal, {
        report: activeReport.value,
        token: activeToken.value,
        open: viewerOpen.value,
        onClose: closeReport
      }, null, 8, ["report", "token", "open"])
    ]),
    _: 1
  }))
}
}

})